/**
 * ProductOverlay Component
 * Full-screen overlay for product quick view
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../features/cart/cartSlice';
import { useToast } from '../ui/Toast';
import { useGA4 } from '../../contexts/GA4Context';
import { getAllImageUrls } from '../../utils/imageUtils';
import {
  findStorefrontVariantBySelection,
  getStorefrontSelectedVariantAvailability,
  getStorefrontVariantAvailability,
  getStorefrontVariantIdentity
} from '../../utils/storefrontVariantUtils';
import UnitQuantityInput from '../UnitQuantityInput';
import './ProductOverlay.css';

// Check if product has variable pricing
function hasVariablePricing(product) {
  if (product?.variantMatrix?.variants?.length > 0) return true;
  if (product?.variations?.length > 0 && product.variations.some(v => v.values?.length > 0)) return true;
  return false;
}

function ProductOverlay({
  isOpen,
  onClose,
  product,
  store,
  themeColors = {},
  formatPrice,
}) {
  const dispatch = useDispatch();
  const toast = useToast();
  const { trackViewItem, trackAddToCart, trackSelectItem } = useGA4();

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [unitQuantity, setUnitQuantity] = useState(0.1);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [currentPrice, setCurrentPrice] = useState(product?.price || 0);

  // Check if product is sold by unit (kg, yard, meter, etc.)
  const isUnitBased = useMemo(() => {
    return product?.sellByUnit === true && product?.unitOfMeasure && product?.unitOfMeasure !== 'PIECE';
  }, [product?.sellByUnit, product?.unitOfMeasure]);

  const {
    primaryColor = '#2563eb',
    textColor = '#111827',
    mutedColor = '#6b7280',
    borderColor = '#e5e7eb',
    bgAlt = '#f9fafb',
    headingFont = 'inherit',
    bodyFont = 'inherit',
  } = themeColors;

  // Reset state when product changes
  useEffect(() => {
    if (product) {
      setSelectedImage(0);
      setQuantity(1);
      setUnitQuantity(parseFloat(product.minOrderQuantity) || 0.1);
      setSelectedVariations({});
      setCurrentPrice(product.price || 0);
    }
  }, [product?.id, product?.minOrderQuantity]);

  // Track view_item when overlay opens with a product
  useEffect(() => {
    if (isOpen && product) {
      trackViewItem({
        id: product.id || product._id,
        name: product.name,
        price: product.price,
        category: product.category || '',
        currency: store?.currency || 'NGN'
      });
      trackSelectItem(product, 'Product Overlay');
    }
  }, [isOpen, product?.id, trackViewItem, trackSelectItem, product, store?.currency]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle back button
  useEffect(() => {
    const handlePopState = () => {
      if (isOpen) {
        onClose();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, onClose]);

  // Get product images using imageUtils
  const images = useMemo(() => {
    return getAllImageUrls(product);
  }, [product]);

  // Get variations (legacy or new format)
  // New format: product.variations = { attributes: [...], variants: [...] }
  // Legacy format: product.variations = [{ name: "Size", values: [...] }]
  const getVariations = () => {
    if (product?.variantMatrix?.attributes) {
      return product.variantMatrix.attributes;
    }
    if (product?.variations) {
      // Check if it's the new variant matrix format (object with attributes)
      if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
        return product.variations.attributes || [];
      }
      // Legacy array format
      if (Array.isArray(product.variations)) {
        return product.variations;
      }
    }
    return [];
  };

  const variations = getVariations();

  // Check if all variations selected
  const allVariationsSelected = !Array.isArray(variations) || variations.length === 0 ||
    variations.every(v => selectedVariations[v.name || v.type]);

  // Handle variation selection
  const handleVariationSelect = (variationType, value) => {
    setSelectedVariations(prev => {
      const updated = {
        ...prev,
        [variationType]: value
      };

      const matchingVariant = findStorefrontVariantBySelection(product, updated);
      if (matchingVariant?.price !== undefined && matchingVariant?.price !== null) {
        setCurrentPrice(parseFloat(matchingVariant.price) || 0);
      }

      return updated;
    });
  };

  const selectedVariantAvailability = allVariationsSelected
    ? getStorefrontSelectedVariantAvailability(product, selectedVariations)
    : null;
  const storefrontAvailability = getStorefrontVariantAvailability(product);
  const activeAvailability = selectedVariantAvailability || storefrontAvailability;
  const availableQuantity = activeAvailability?.availableQuantity ?? Number(product?.stock ?? product?.quantity ?? 0);
  const isOutOfStock = availableQuantity <= 0;

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!allVariationsSelected) {
      toast.error('Please select all options');
      return;
    }

    try {
      const productImage = images.length > 0 && !images[0]?.includes('placeholder') ? images[0] : null;
      const finalQuantity = isUnitBased ? unitQuantity : quantity;
      const matchingVariant = findStorefrontVariantBySelection(product, selectedVariations);
      // Ordinary products have no matching variant. Keep their cart payload
      // variant-free instead of dereferencing the null identity and failing
      // before the cart request is sent.
      const variantIdentity = getStorefrontVariantIdentity(matchingVariant) || {};

      await dispatch(addToCart({
        product: {
          _id: product._id || product.id,
          id: product.id || product._id,
          name: product.name,
          price: parseFloat(currentPrice),
          image: productImage,
          storeSlug: store?.slug,
          variations: Object.keys(selectedVariations).length > 0 ? selectedVariations : null,
          variantId: variantIdentity.variantId || undefined,
          variantSku: variantIdentity.variantSku || undefined,
          variantName: variantIdentity.variantName || undefined,
          variantCombination: variantIdentity.variantCombination || undefined,
          // Unit-based selling info
          sellByUnit: isUnitBased || undefined,
          unitOfMeasure: isUnitBased ? product.unitOfMeasure : undefined,
          pricePerUnit: isUnitBased ? parseFloat(currentPrice) : undefined,
          unitQuantity: isUnitBased ? unitQuantity : undefined
        },
        quantity: finalQuantity
      })).unwrap();

      // Track GA4 add_to_cart event
      trackAddToCart({
        id: product.id || product._id,
        name: product.name,
        price: currentPrice,
        category: product.category || '',
        currency: store?.currency || 'NGN'
      }, finalQuantity);

      toast.success(`${product.name} added to cart`);
      onClose();
    } catch (error) {
      toast.error('Failed to add to cart. Please try again.');
    }
  };

  if (!isOpen || !product) return null;
  const hasVariants = hasVariablePricing(product);

  return (
    <div
      className="po-overlay"
      style={{
        '--po-primary': primaryColor,
        '--po-text': textColor,
        '--po-muted': mutedColor,
        '--po-border': borderColor,
        '--po-bg-alt': bgAlt,
      }}
    >
      {/* Close Button - Fixed at page top-right */}
      <button className="po-close" onClick={onClose} aria-label="Close">
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      <div className="po-container">

        <div className="po-content">
          {/* Image Gallery */}
          <div className="po-gallery">
            <div className="po-gallery__main">
              {images.length > 0 && images[selectedImage] && !images[selectedImage].includes('placeholder') ? (
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="po-gallery__image"
                />
              ) : (
                <div className="po-gallery__placeholder">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
              )}

              {/* Out of Stock Badge */}
              {isOutOfStock && (
                <span className="po-badge po-badge--out-of-stock">Out of Stock</span>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && !images[0]?.includes('placeholder') && (
              <div className="po-gallery__thumbnails">
                {images.map((imgUrl, index) => (
                  <button
                    key={index}
                    className={`po-gallery__thumb ${selectedImage === index ? 'po-gallery__thumb--active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={imgUrl} alt={`${product.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="po-info">
            <h1 className="po-info__name" style={{ fontFamily: headingFont }}>
              {product.name}
            </h1>

            <p className="po-info__price" style={{ color: primaryColor }}>
              {formatPrice(currentPrice)}
              {!hasVariants && product.compareAtPrice && parseFloat(product.compareAtPrice) > parseFloat(product.price) && (
                <span className="po-info__compare-price">
                  {formatPrice(product.compareAtPrice)}
                </span>
              )}
            </p>

            {/* Variations - Dropdown Style */}
            {variations.length > 0 && (
              <div className="po-variations">
                {variations.map((variation) => {
                  const variationType = variation.name || variation.type;
                  const values = variation.values || variation.options || [];

                  return (
                    <div key={variationType} className="po-variation">
                      <label className="po-variation__label">{variationType}</label>
                      <div className="po-variation__dropdown-wrapper">
                        <select
                          className="po-variation__dropdown"
                          value={selectedVariations[variationType] || ''}
                          onChange={(e) => handleVariationSelect(variationType, e.target.value)}
                          style={{ borderColor: selectedVariations[variationType] ? primaryColor : borderColor }}
                        >
                          <option value="">Select {variationType}</option>
                          {values.map((value) => {
                            const valueName = typeof value === 'string' ? value : value.name || value.value;
                            return (
                              <option key={valueName} value={valueName}>
                                {valueName}
                              </option>
                            );
                          })}
                        </select>
                        <svg className="po-variation__dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quantity - Show UnitQuantityInput for unit-based products */}
            {isUnitBased ? (
              <UnitQuantityInput
                unitOfMeasure={product.unitOfMeasure}
                pricePerUnit={currentPrice}
                quantity={unitQuantity}
                onQuantityChange={setUnitQuantity}
                minQuantity={parseFloat(product.minOrderQuantity) || 0.1}
                stepIncrement={parseFloat(product.stepIncrement) || 0.1}
                formatPrice={formatPrice}
                primaryColor={primaryColor}
              />
            ) : (
              <div className="po-quantity">
                <label className="po-quantity__label">Quantity</label>
                <div className="po-quantity__controls">
                  <button
                    className="po-quantity__btn"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14" />
                    </svg>
                  </button>
                  <span className="po-quantity__value">{quantity}</span>
                  <button
                    className="po-quantity__btn"
                    onClick={() => setQuantity(quantity + 1)}
                    disabled={quantity >= (availableQuantity ?? 999)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Add to Cart Button */}
            <button
              className={`po-add-to-cart ${isOutOfStock ? 'po-add-to-cart--disabled' : ''}`}
              onClick={handleAddToCart}
              disabled={isOutOfStock || !allVariationsSelected}
              style={{ backgroundColor: primaryColor }}
            >
              {isOutOfStock ? 'Out of Stock' : !allVariationsSelected ? 'Select Options' : 'Add to Cart'}
            </button>

            {/* Description */}
            {product.description && (
              <div className="po-description" style={{ fontFamily: bodyFont }}>
                <h3 className="po-description__title">Description</h3>
                <p className="po-description__text">{product.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductOverlay;
